import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "../../components/Toast";

type SitemapEntry = {
  loc: string;
  lastmod: string;
  changefreq: string;
  priority: string;
};

const today = () => new Date().toISOString().slice(0, 10);

const toEntry = (url: string): SitemapEntry => ({
  loc: url,
  lastmod: today(),
  changefreq: "weekly",
  priority: "0.5",
});

const emptyEntry = (): SitemapEntry => ({
  loc: "",
  lastmod: "",
  changefreq: "weekly",
  priority: "0.5",
});

const CHANGEFREQ_OPTIONS = [
  "always",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
  "never",
] as const;

const PRIORITY_OPTIONS = [
  "0.0",
  "0.1",
  "0.2",
  "0.3",
  "0.4",
  "0.5",
  "0.6",
  "0.7",
  "0.8",
  "0.9",
  "1.0",
] as const;

export function SitemapGenerator() {
  const { t } = useTranslation();

  // Crawl state
  const [crawlUrl, setCrawlUrl] = useState("");
  const [crawling, setCrawling] = useState(false);
  const [crawlError, setCrawlError] = useState<string | null>(null);

  // Entries state
  const [entries, setEntries] = useState<SitemapEntry[]>([emptyEntry()]);
  const [includeLastmod, setIncludeLastmod] = useState(true);
  const [includeChangefreq, setIncludeChangefreq] = useState(true);
  const [includePriority, setIncludePriority] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleCrawl = async () => {
    const trimmed = crawlUrl.trim();
    if (!trimmed) return;

    setCrawling(true);
    setCrawlError(null);

    try {
      const params = new URLSearchParams({ url: trimmed, max: "50" });
      const response = await fetch(`/api/crawl?${params}`);
      const data = await response.json();

      if (!data.ok) {
        setCrawlError(data.error || t("sitemapGenerator.crawlFailed"));
        return;
      }

      const urls: string[] = data.urls;
      if (urls.length === 0) {
        setCrawlError(t("sitemapGenerator.noUrlsFound"));
        return;
      }

      setEntries(urls.map(toEntry));
    } catch {
      setCrawlError(t("sitemapGenerator.crawlFailed"));
    } finally {
      setCrawling(false);
    }
  };

  const sitemapXml = useMemo(() => {
    const validEntries = entries.filter((e) => e.loc.trim());
    if (validEntries.length === 0) return "";

    const lines = [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ];

    for (const entry of validEntries) {
      lines.push("  <url>");
      lines.push(`    <loc>${escapeXml(entry.loc.trim())}</loc>`);
      if (includeLastmod && entry.lastmod) {
        lines.push(`    <lastmod>${escapeXml(entry.lastmod)}</lastmod>`);
      }
      if (includeChangefreq) {
        lines.push(`    <changefreq>${entry.changefreq}</changefreq>`);
      }
      if (includePriority) {
        lines.push(`    <priority>${entry.priority}</priority>`);
      }
      lines.push("  </url>");
    }

    lines.push("</urlset>");
    return lines.join("\n");
  }, [entries, includeLastmod, includeChangefreq, includePriority]);

  const updateEntry = (
    index: number,
    field: keyof SitemapEntry,
    value: string,
  ) => {
    setEntries((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, emptyEntry()]);
  };

  const removeEntry = (index: number) => {
    setEntries((prev) => {
      if (prev.length === 1) return [emptyEntry()];
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const copyToClipboard = async () => {
    if (!sitemapXml) return;
    try {
      await navigator.clipboard.writeText(sitemapXml);
      setToastMessage(t("sitemapGenerator.copied"));
    } catch {
      setToastMessage(t("sitemapGenerator.copyFailed"));
    }
  };

  const downloadSitemap = () => {
    if (!sitemapXml) return;
    const blob = new Blob([sitemapXml], {
      type: "application/xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sitemap.xml";
    link.click();
    URL.revokeObjectURL(url);
  };

  const urlCount = entries.filter((e) => e.loc.trim()).length;

  return (
    <div className="sitemap-generator">
      {/* Crawl section */}
      <div className="sg-card">
        <span className="sg-label">{t("sitemapGenerator.crawlLabel")}</span>
        <p className="sg-hint">{t("sitemapGenerator.crawlHint")}</p>
        <div className="sg-crawl-row">
          <input
            className="sg-input"
            type="url"
            value={crawlUrl}
            onChange={(e) => setCrawlUrl(e.target.value)}
            placeholder={t("sitemapGenerator.crawlPlaceholder")}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !crawling) handleCrawl();
            }}
          />
          <button
            type="button"
            className="sg-primary-btn"
            onClick={handleCrawl}
            disabled={crawling || !crawlUrl.trim()}
          >
            {crawling
              ? t("sitemapGenerator.crawling")
              : t("sitemapGenerator.crawl")}
          </button>
        </div>
        {crawlError && <p className="sg-error">{crawlError}</p>}
      </div>

      {/* Options */}
      <div className="sg-options-card">
        <span className="sg-label">{t("sitemapGenerator.optionsLabel")}</span>
        <div className="sg-options-row">
          <label className="sg-checkbox-label">
            <input
              type="checkbox"
              checked={includeLastmod}
              onChange={(e) => setIncludeLastmod(e.target.checked)}
            />
            {t("sitemapGenerator.includeLastmod")}
          </label>
          <label className="sg-checkbox-label">
            <input
              type="checkbox"
              checked={includeChangefreq}
              onChange={(e) => setIncludeChangefreq(e.target.checked)}
            />
            {t("sitemapGenerator.includeChangefreq")}
          </label>
          <label className="sg-checkbox-label">
            <input
              type="checkbox"
              checked={includePriority}
              onChange={(e) => setIncludePriority(e.target.checked)}
            />
            {t("sitemapGenerator.includePriority")}
          </label>
        </div>
      </div>

      {/* URL entries */}
      <div className="sg-card">
        <div className="sg-card-header">
          <span className="sg-label">
            {t("sitemapGenerator.urlsLabel")}
            {urlCount > 0 && (
              <span className="sg-url-count">
                {" "}
                ({t("sitemapGenerator.urlCount", { count: urlCount })})
              </span>
            )}
          </span>
          <button type="button" className="sg-add-btn" onClick={addEntry}>
            {t("sitemapGenerator.addUrl")}
          </button>
        </div>
        <div className="sg-entries">
          {entries.map((entry, index) => (
            <div key={`entry-${index}`} className="sg-entry">
              <div className="sg-entry-header">
                <span className="sg-entry-number">#{index + 1}</span>
                <button
                  type="button"
                  className="sg-remove-btn"
                  onClick={() => removeEntry(index)}
                  aria-label={t("sitemapGenerator.removeUrl")}
                >
                  ×
                </button>
              </div>
              <div className="sg-entry-fields">
                <div className="sg-field sg-field-full">
                  <label className="sg-field-label">
                    {t("sitemapGenerator.urlLabel")}
                  </label>
                  <input
                    className="sg-input"
                    type="url"
                    value={entry.loc}
                    onChange={(e) => updateEntry(index, "loc", e.target.value)}
                    placeholder={t("sitemapGenerator.urlPlaceholder")}
                  />
                </div>
                {includeLastmod && (
                  <div className="sg-field">
                    <label className="sg-field-label">
                      {t("sitemapGenerator.lastmodLabel")}
                    </label>
                    <input
                      className="sg-input"
                      type="date"
                      value={entry.lastmod}
                      onChange={(e) =>
                        updateEntry(index, "lastmod", e.target.value)
                      }
                    />
                  </div>
                )}
                {includeChangefreq && (
                  <div className="sg-field">
                    <label className="sg-field-label">
                      {t("sitemapGenerator.changefreqLabel")}
                    </label>
                    <select
                      className="sg-select"
                      value={entry.changefreq}
                      onChange={(e) =>
                        updateEntry(index, "changefreq", e.target.value)
                      }
                    >
                      {CHANGEFREQ_OPTIONS.map((freq) => (
                        <option key={freq} value={freq}>
                          {t(`sitemapGenerator.changefreq.${freq}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {includePriority && (
                  <div className="sg-field">
                    <label className="sg-field-label">
                      {t("sitemapGenerator.priorityLabel")}
                    </label>
                    <select
                      className="sg-select"
                      value={entry.priority}
                      onChange={(e) =>
                        updateEntry(index, "priority", e.target.value)
                      }
                    >
                      {PRIORITY_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="sg-preview-card">
        <div className="sg-card-header">
          <span className="sg-label">{t("sitemapGenerator.previewLabel")}</span>
          <div className="sg-actions">
            <button
              type="button"
              className="sg-secondary-btn"
              onClick={copyToClipboard}
              disabled={!sitemapXml}
            >
              {t("sitemapGenerator.copy")}
            </button>
            <button
              type="button"
              className="sg-secondary-btn"
              onClick={downloadSitemap}
              disabled={!sitemapXml}
            >
              {t("sitemapGenerator.download")}
            </button>
          </div>
        </div>
        <pre className="sg-preview">
          {sitemapXml || t("sitemapGenerator.emptyPreview")}
        </pre>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
